import React, { useState, useRef, useEffect, useMemo } from "react";
import Pagination from "../../components/Pagination";
import { Icon } from "@iconify/react";
import { getJobStatusDisplay } from "../../../utils/jobStatusDisplay";
import { Link, useLocation } from "react-router-dom";
import moment from "moment";
import { DatePicker, ConfigProvider } from "antd";
import thTH from "antd/locale/th_TH";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/th";
import SearchableSelect from "../SearchableSelect";

dayjs.locale("th");
dayjs.extend(customParseFormat);

export default function JobListTable({
  jobs = [],
  onView,
  onEdit,
  onDelete,
  statusFilter = "all",
  teamFilter = "all",
  onStatusFilterChange,
  onTeamFilterChange,
  startDate = "",
  endDate = "",
  onStartDateChange,
  onEndDateChange,
  serviceDateMode = "year",
  onServiceDateModeChange,
  fullHeight = false,
  branchFilter = "all",
  onBranchFilterChange,
}) {
  const getRowStatus = (row) =>
    String(row?.Job_Status ?? row?.jobStatus ?? row?.JobStatus ?? "").trim();

  const normalizeJobStatus = (status) => {
    const raw = String(status || "").trim();
    if (!raw) return "";
    if (raw === "ลงงาน") return "ลงงาน";
    if (raw === "working" || raw === "Check in ทำงาน" || raw === "On-Site" || raw === "กำลังทำงาน") {
      return "กำลังทำงาน";
    }
    if (raw === "ปิดงาน" || raw === "ปิดงาน เซ็นแล้ว" || raw === "ปิดงาน รอเซ็น") {
      return "ปิดงาน รอเซ็น";
    }
    if (raw === "รอส่งงาน") {
      return "ส่งงาน";
    }
    if (raw === "ส่งงาน (Checkout)" || raw === "ส่งงาน") {
      return "ส่งงาน";
    }
    if (raw === "สำเร็จ") return "สำเร็จ";
    return raw;
  };

  const isStatusMatch = (jobStatus, selectedFilter) => {
    if (selectedFilter === "all") return true;
    const normalized = normalizeJobStatus(jobStatus);
    if (selectedFilter === "กำลังทำงาน") {
      return normalized === "กำลังทำงาน";
    }
    if (selectedFilter === "ส่งงาน") {
      return normalized === "ส่งงาน";
    }
    return normalized === selectedFilter;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "Contract_No", direction: "desc" });
  const [selectedRowKey, setSelectedRowKey] = useState("");
  const location = useLocation();

  const actionRef = useRef(null);
  const [rightOffset, setRightOffset] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const s = params.get("search");
    const p = Number.parseInt(params.get("page") || "1", 10);
    const ps = Number.parseInt(params.get("pageSize") || "100", 10);
    const focus = params.get("focus") || "";
    const nextSearch = s || "";
    const nextPage = Number.isFinite(p) && p > 0 ? p : 1;
    const nextPageSize = Number.isFinite(ps) && ps > 0 ? ps : 100;
    if (search !== nextSearch) setSearch(nextSearch);
    if (searchInput !== nextSearch) setSearchInput(nextSearch);
    if (currentPage !== nextPage) setCurrentPage(nextPage);
    if (itemsPerPage !== nextPageSize) setItemsPerPage(nextPageSize);
    if (selectedRowKey !== focus) setSelectedRowKey(focus);
  }, [location.search]);

  const getRowFocusKey = (row) => {
    const roundId = row?.Round_Id ?? row?.round_id ?? "";
    const jobId = row?.JobId ?? row?.job_id ?? "";
    const roundNo = row?.Round_No ?? row?.round_no ?? "";
    if (roundId !== null && roundId !== undefined && String(roundId) !== "") return `rid:${String(roundId)}`;
    return `jno:${String(jobId)}__${String(roundNo)}`;
  };

  const buildJobManagementBackLink = (focusKey) => {
    const params = new URLSearchParams(location.search);
    if (search) params.set("search", search);
    else params.delete("search");
    params.set("page", String(currentPage || 1));
    params.set("pageSize", String(itemsPerPage || 100));
    if (focusKey) params.set("focus", focusKey);
    else params.delete("focus");
    const qs = params.toString();
    return `/job-management${qs ? `?${qs}` : ""}`;
  };

  const teamOptions = useMemo(() => {
    const seen = new Set();
    const options = [{ value: "all", label: "ทีม: ทั้งหมด" }];

    for (const job of jobs) {
      const teamId = job?.TeamId;
      const teamName = String(job?.TeamName || "").trim();
      if (teamId == null || !teamName) continue;
      const key = String(teamId);
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({ value: key, label: teamName });
    }

    return options.sort((a, b) => {
      if (a.value === "all") return -1;
      if (b.value === "all") return 1;
      return a.label.localeCompare(b.label, "th");
    });
  }, [jobs]);

  const getRowBranchId = (row) =>
    row?.Branch_Id ??
    row?.BranchId ??
    row?.branch_id ??
    row?.branchId ??
    null;

  const getRowBranchName = (row) =>
    String(
      row?.Branch_Name ??
        row?.BranchName ??
        row?.branch_name ??
        row?.branchName ??
        ""
    ).trim();

  const branchOptions = useMemo(() => {
    const seen = new Set();
    const options = [{ value: "all", label: "สาขา: ทั้งหมด" }];
    for (const job of jobs) {
      const branchId = getRowBranchId(job);
      const branchName = getRowBranchName(job);
      if (branchId == null || !branchName) continue;
      const key = String(branchId);
      if (seen.has(key)) continue;
      seen.add(key);
      options.push({ value: key, label: branchName });
    }
    return options.sort((a, b) => {
      if (a.value === "all") return -1;
      if (b.value === "all") return 1;
      return a.label.localeCompare(b.label, "th");
    });
  }, [jobs]);


  const filtered = jobs.filter((x) => {
    // Filter by search query
    const key = search.toLowerCase();
    const matchesSearch = 
      (x.CustomerName || "").toLowerCase().includes(key) ||
      (x.Contract_No || "").toLowerCase().includes(key) ||
      (x.ServiceName || "").toLowerCase().includes(key) ||
      (x.ServiceAddr_Name || "").toLowerCase().includes(key);

    // Filter by job status
    const matchesStatus = isStatusMatch(getRowStatus(x), statusFilter);

    // Filter by team (ชื่อทีม)
    let matchesTeam = true;
    if (teamFilter !== "all") {
      matchesTeam = String(x.TeamId) === teamFilter;
    }

    // Filter by date range (วันที่เข้าบริการ)
    let matchesDateRange = true;
    if (startDate || endDate) {
      if (!x.ServiceDate) {
        matchesDateRange = false;
      } else {
        const serviceDate = moment(x.ServiceDate);
        if (startDate && endDate) {
          const start = moment(startDate);
          const end = moment(endDate);
          matchesDateRange = serviceDate.isBetween(start, end, 'day', '[]'); // inclusive
        } else if (startDate) {
          const start = moment(startDate);
          matchesDateRange = serviceDate.isSameOrAfter(start, 'day');
        } else if (endDate) {
          const end = moment(endDate);
          matchesDateRange = serviceDate.isSameOrBefore(end, 'day');
        }
      }
    }

    let matchesBranch = true;
    if (branchFilter !== "all") {
      matchesBranch = String(getRowBranchId(x) ?? "") === String(branchFilter);
    }

    return (
      matchesSearch &&
      matchesStatus &&
      matchesTeam &&
      matchesDateRange &&
      matchesBranch
    );
  });

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Apply sorting
  const sorted = [...filtered].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === "Job_Status") {
      aValue = getRowStatus(a);
      bValue = getRowStatus(b);
    }

    // Handle null/undefined values
    if (aValue == null) aValue = '';
    if (bValue == null) bValue = '';

    // Special handling for dates
    if (sortConfig.key === 'ServiceDate' || sortConfig.key === 'AssignedDate') {
      const aDate = aValue ? new Date(aValue) : new Date(0);
      const bDate = bValue ? new Date(bValue) : new Date(0);
      if (sortConfig.direction === 'asc') {
        return aDate - bDate;
      } else {
        return bDate - aDate;
      }
    }

    // Special handling for numbers (Round_No)
    if (sortConfig.key === 'Round_No') {
      const aNum = Number(aValue) || 0;
      const bNum = Number(bValue) || 0;
      if (sortConfig.direction === 'asc') {
        return aNum - bNum;
      } else {
        return bNum - aNum;
      }
    }

    // Convert to string for comparison
    if (typeof aValue !== 'string') aValue = String(aValue);
    if (typeof bValue !== 'string') bValue = String(bValue);

    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue, 'th', { numeric: true });
    } else {
      return bValue.localeCompare(aValue, 'th', { numeric: true });
    }
  });

  const totalItems = sorted.length;
  const from = (currentPage - 1) * itemsPerPage;
  const paginated = sorted.slice(from, from + itemsPerPage);

  const formatDate = (d, jobStatus = "") => {
    if (!d) return "-";
    try {
      const date = moment(d);
      if (!date.isValid()) return "-";

      const workedStatuses = new Set([
        "Check in ทำงาน",
        "working",
        "On-Site",
        "ปิดงาน รอเซ็น",
        "ปิดงาน เซ็นแล้ว",
        "ปิดงาน",
        "รอส่งงาน",
        "ส่งงาน",
        "ส่งงาน (Checkout)",
        "สำเร็จ",
      ]);

      const status = String(jobStatus || "").trim();
      const year = date.year() + 543; // แปลงเป็น พ.ศ.
      const month = String(date.month() + 1).padStart(2, "0");

      // ถ้ามีการทำงานแล้ว ให้แสดงวัน/เดือน/ปี
      if (workedStatuses.has(status)) {
        const day = String(date.date()).padStart(2, "0");
        return `${day}/${month}/${year}`;
      }

      // กรณียังไม่ทำงาน แสดงเดือน/ปี
      return `${month}/${year}`;
    } catch {
      return "-";
    }
  };


  useEffect(() => {
    if (actionRef.current) {
      setRightOffset(actionRef.current.getBoundingClientRect().width);
    }
  }, []);

  // Helper function to convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  // Helper function to get text color based on background brightness
  const getTextColor = (backgroundColor) => {
    if (!backgroundColor) return '#1f2937';
    const hex = backgroundColor.startsWith('#') ? backgroundColor : `#${backgroundColor}`;
    const rgb = hexToRgb(hex);
    if (rgb) {
      const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
      return brightness > 128 ? '#1f2937' : '#ffffff';
    }
    return '#1f2937';
  };

  // Helper function to get cell props for team column
  const getTeamCellProps = (teamColor) => {
    if (!teamColor || teamColor.trim() === '') return {};
    const bgColor = teamColor.startsWith('#') ? teamColor : `#${teamColor}`;
    const textColor = getTextColor(bgColor);
    return {
      'data-team-color': 'true',
      style: {
        '--team-bg-color': bgColor,
        '--team-text-color': textColor,
      }
    };
  };

  const getStatusBadge = (jobStatus) => {
    const normalizedStatus = normalizeJobStatus(jobStatus);
    if (!normalizedStatus) {
      return (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm font-medium">
          รอมอบหมายงาน
        </span>
      );
    }
    
    // สีแยกตามสถานะ (แต่ละสถานะสีต่างกัน)
    const statusColors = {
      'ลงงาน': 'bg-slate-100 text-slate-800',           // 1. เทา
      'Check in ทำงาน': 'bg-blue-100 text-blue-800',    // 2. น้ำเงิน
      'working': 'bg-blue-100 text-blue-800',
      'On-Site': 'bg-blue-100 text-blue-800',
      'กำลังทำงาน': 'bg-blue-100 text-blue-800',
      'ปิดงาน รอเซ็น': 'bg-amber-100 text-amber-800',   // 3. ส้มอำพัน
      'ปิดงาน': 'bg-amber-100 text-amber-800',
      'รอส่งงาน': 'bg-teal-100 text-teal-800',         // สีเขียวอมฟ้า (แยกจากปิดงาน)
      'ส่งงาน': 'bg-purple-100 text-purple-800',       // 4. ม่วง
      'ส่งงาน (Checkout)': 'bg-purple-100 text-purple-800',
      'สำเร็จ': 'bg-green-100 text-green-800',          // 5. เขียว
      'ยกเลิก': 'bg-red-100 text-red-800',
      'เกิดเวลา': 'bg-orange-100 text-orange-800',
    };
    
    const colorClass = statusColors[normalizedStatus] || 'bg-gray-100 text-gray-800';
    
    const displayLabel = getJobStatusDisplay(normalizedStatus);
    return (
      <span className={`px-2 py-1 ${colorClass} rounded text-sm font-medium`}>
        {displayLabel}
      </span>
    );
  };

  const getTeamBadge = (teamName, teamId, teamColor) => {
    if (!teamName) return <span className="text-gray-400">-</span>;
    
    // ถ้ามี TeamColor ให้แสดง badge ที่มี background color
    if (teamColor) {
      const bgColor = teamColor.startsWith('#') ? teamColor : `#${teamColor}`;
      const textColor = getTextColor(bgColor);
      return (
        <span 
          className="team-badge text-sm font-medium"
          style={{
            backgroundColor: bgColor,
            color: textColor,
          }}
        >
          {teamName}
        </span>
      );
    }
    
    // Fallback ใช้สีตาม teamId ถ้าไม่มี TeamColor
    const colors = {
      1: "bg-blue-100 text-blue-800",
      2: "bg-green-100 text-green-800",
      3: "bg-yellow-100 text-yellow-800",
    };
    const colorClass = colors[teamId] || "bg-gray-100 text-gray-800";
    return (
      <span className={`px-2 py-1 rounded text-sm font-medium ${colorClass}`}>
        {teamName}
      </span>
    );
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <Icon icon="mdi:unfold-more-horizontal" className="w-4 h-4 inline ml-1" />;
    }
    return sortConfig.direction === 'asc' 
      ? <Icon icon="mdi:chevron-up" className="w-4 h-4 inline ml-1" />
      : <Icon icon="mdi:chevron-down" className="w-4 h-4 inline ml-1" />;
  };

  return (
    <div
      className="card table-card border-0 rounded-0 shadow-sm"
      style={{
        display: "flex",
        flexDirection: "column",
        height: fullHeight ? "100%" : undefined,
        maxHeight: fullHeight ? "none" : "calc(100vh - 400px)",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <style>{`
        * { font-size: 14px !important; }

        .table-wrapper-scroll { 
          padding: 0 0 0 20px !important; 
        }

        table.table {
            border-collapse: separate !important;
            border-spacing: 1px !important;
            width: 100%;
            min-width: 1500px;
        }

        th, td {
            padding: 8px 10px !important;
            background: white !important;
            text-align: center;
            outline: 1px solid #d0d0d0;
            outline-offset: -1px;
            white-space: nowrap;
        }

        /* Team column - background should only cover text, not full td */
        table.table tbody tr td[data-team-color="true"] {
            background: white !important;
            color: #1f2937 !important;
        }
        
        /* Team badge with background color */
        table.table tbody tr td[data-team-color="true"] .team-badge {
            background: var(--team-bg-color) !important;
            color: var(--team-text-color) !important;
            display: inline-block;
            padding: 4px 8px;
            border-radius: 4px;
        }
        table.table tbody tr.active-focus-row td {
            background: #fce8eb !important;
        }

        .service-date-filter-row {
          display: flex;
          align-items: center;
          flex-wrap: nowrap;
          gap: 8px;
        }
        .service-date-mode-group {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-left: 2px;
          white-space: nowrap;
          width: fit-content;
          flex: 0 0 auto;
        }
        .service-date-mode-group label,
        .service-date-mode-group input {
          font-size: 14px !important;
        }
        .service-date-mode-group label {
          display: inline-flex;
          align-items: center;
          gap: 0 !important;
          width: 80px;
          min-width: 80px;
          margin: 0;
          line-height: 1;
        }
        .service-date-mode-group input[type="radio"] {
          width: 12px;
          height: 12px;
          margin: 0 5px 0 0;
          vertical-align: middle;
        }

        /* --- HEADER STYLE --- */
        thead th {
            background-color: #E13F57 !important;
            color: white !important;
            font-weight: 600;
            text-align: center;
            position: sticky;
            top: 0;
            z-index: 10;
        }

        /* ให้ sticky ก็ยังเป็นสีเดียวกัน */
        td.sticky-action, th.sticky-action {
            position: sticky;
            right: 0;
            background: white !important;
            z-index: 20;
            isolation: isolate;
            box-shadow: -3px 0 5px rgba(0,0,0,0.12);
            padding: 8px 12px 8px 10px !important;
        }

        tbody td.sticky-action {
            background: white !important;
        }

        tbody tr:hover td.sticky-action {
            background: #f5f7ff !important;
        }

        thead th.sticky-action {
            background-color: #E13F57 !important;
            z-index: 21 !important;
        }
        `}</style>

      <div className="d-flex justify-content-between align-items-center px-4 py-2 border-bottom">
        <div className="d-flex align-items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="ค้นหา..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setSearch(searchInput);
                  setCurrentPage(1);
                }
              }}
              className="h-[36px] w-[250px] border border-gray-300 rounded-md px-3 pr-8 py-1 text-sm"
            />

            {searchInput !== "" && (
              <button
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                  setCurrentPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Job Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              onStatusFilterChange?.(e.target.value);
              setCurrentPage(1);
            }}
            className="h-[36px] border border-gray-300 rounded-md px-3 py-1 text-sm"
          >
            <option value="all">สถานะงาน: ทั้งหมด</option>
            <option value="">รอมอบหมายงาน</option>
            <option value="ลงงาน">{getJobStatusDisplay("ลงงาน")}</option>
            <option value="กำลังทำงาน">กำลังทำงาน</option>
            <option value="ปิดงาน รอเซ็น">{getJobStatusDisplay("ปิดงาน รอเซ็น")}</option>
            <option value="ส่งงาน">{getJobStatusDisplay("ส่งงาน")}</option>
            <option value="สำเร็จ">{getJobStatusDisplay("สำเร็จ")}</option>
          </select>

          {/* Team Filter */}
          <div style={{ minWidth: "220px" }}>
            <SearchableSelect
              value={teamFilter}
              onChange={(value) => {
                onTeamFilterChange?.(String(value));
                setCurrentPage(1);
              }}
              options={teamOptions}
              placeholder="ทีม: ทั้งหมด"
              searchPlaceholder="ค้นหาชื่อทีม..."
              className="h-[36px]"
              triggerClassName="text-sm"
              triggerStyle={{ height: "36px", paddingTop: "4px", paddingBottom: "4px" }}
              renderValue={(selected) => selected?.label || "ทีม: ทั้งหมด"}
            />
          </div>

          {/* Date Range Filter */}
          <div className="service-date-filter-row">
            <label className="text-sm text-gray-600 whitespace-nowrap" style={{ width: '100px' }}>วันที่เข้าบริการ:</label>
            <ConfigProvider locale={thTH}>
              <DatePicker
                value={startDate ? dayjs(startDate, "YYYY-MM-DD") : null}
                onChange={(date) => {
                  onStartDateChange?.(date ? date.format("YYYY-MM-DD") : "");
                  setCurrentPage(1);
                }}
                format="DD/MM/YYYY"
                placeholder="จากวันที่"
                className="h-[36px] ml-1"
                style={{ width: '130px' }}
                allowClear
              />
              <span className="text-gray-400 mx-2">-</span>
              <DatePicker
                value={endDate ? dayjs(endDate, "YYYY-MM-DD") : null}
                onChange={(date) => {
                  onEndDateChange?.(date ? date.format("YYYY-MM-DD") : "");
                  setCurrentPage(1);
                }}
                format="DD/MM/YYYY"
                placeholder="ถึงวันที่"
                className="h-[36px]"
                style={{ width: '130px' }}
                allowClear
              />
            </ConfigProvider>
            <div className="service-date-mode-group">
              <label className="text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="service-date-mode"
                  checked={serviceDateMode === "all"}
                  onChange={() => onServiceDateModeChange?.("all")}
                />
                ทั้งหมด
              </label>
              <label className="text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="service-date-mode"
                  checked={serviceDateMode === "day"}
                  onChange={() => onServiceDateModeChange?.("day")}
                />
                รายวัน
              </label>
              <label className="text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="service-date-mode"
                  checked={serviceDateMode === "month"}
                  onChange={() => onServiceDateModeChange?.("month")}
                />
                รายเดือน
              </label>
              <label className="text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="service-date-mode"
                  checked={serviceDateMode === "year"}
                  onChange={() => onServiceDateModeChange?.("year")}
                />
                รายปี
              </label>
            </div>
            {(startDate || endDate) && (
              <button
                onClick={() => {
                  onStartDateChange?.("");
                  onEndDateChange?.("");
                  setCurrentPage(1);
                }}
                className="text-gray-400 hover:text-gray-600 ml-2"
                title="ล้างตัวกรองวันที่"
              >
                <Icon icon="mdi:close" className="w-4 h-4" />
              </button>
            )}
          </div>

          <div style={{ minWidth: "220px" }}>
            <SearchableSelect
              value={branchFilter}
              onChange={(value) => {
                onBranchFilterChange?.(String(value));
                setCurrentPage(1);
              }}
              options={branchOptions}
              placeholder="สาขา: ทั้งหมด"
              searchPlaceholder="ค้นหาชื่อสาขา..."
              className="h-[36px]"
              triggerClassName="text-sm"
              triggerStyle={{ height: "36px", paddingTop: "4px", paddingBottom: "4px" }}
              renderValue={(selected) => selected?.label || "สาขา: ทั้งหมด"}
            />
          </div>
        </div>
      </div>

      <div
        className="table-wrapper-scroll flex-1"
        style={{ overflowY: "auto", overflowX: "auto", flexGrow: 1 }}
      >
        <table className="table table-bordered table-hover mb-0">
          <thead>
            <tr>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('index')}
              >
                ลำดับ {getSortIcon('index')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('Round_No')}
              >
                ครั้งที่ {getSortIcon('Round_No')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('TeamName')}
              >
                ทีมช่าง {getSortIcon('TeamName')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('CustomerName')}
              >
                ลูกค้าในสัญญา {getSortIcon('CustomerName')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('Contract_No')}
              >
                เลขที่สัญญา {getSortIcon('Contract_No')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('ServiceAddr_Name')}
              >
                ชื่อสถานที่ให้บริการ {getSortIcon('ServiceAddr_Name')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('ServiceName')}
              >
                บริการ {getSortIcon('ServiceName')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('Job_Status')}
              >
                สถานะJob {getSortIcon('Job_Status')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('ServiceDate')}
              >
                วันที่เข้าบริการ {getSortIcon('ServiceDate')}
              </th>
              <th 
                className="cursor-pointer hover:bg-red-700 select-none"
                onClick={() => handleSort('AssignedTime')}
              >
                เวลา {getSortIcon('AssignedTime')}
              </th>
              <th ref={actionRef} className="sticky-action" style={{ paddingRight: '0' }}>
                การจัดการ
              </th>
            </tr>
          </thead>

          <tbody>
            {paginated.length ? (
              paginated.map((row, i) => (
                <tr
                  key={`${row.Round_Id || row.JobId}-${row.Round_No}-${i}`}
                  className={getRowFocusKey(row) === selectedRowKey ? "active-focus-row" : ""}
                >
                  <td>{from + i + 1}</td>
                  <td>{row.Round_No && row.TotalRounds ? `${row.Round_No}/${row.TotalRounds}` : '-'}</td>
                  <td {...getTeamCellProps(row.TeamColor)}>
                    {getTeamBadge(row.TeamName, row.TeamId, row.TeamColor)}
                  </td>
                  <td className="text-start">{row.CustomerName || "-"}</td>
                  <td className="text-start">
                    {row.Contract_No ? (
                      <Link
                        to={`/contract/edit/${row.JobId}`}
                        state={(() => {
                          const focusKey = getRowFocusKey(row);
                          return {
                            fromJobManagement: true,
                            jobManagementBackLink: buildJobManagementBackLink(focusKey),
                            jobManagementBackLabel: "จัดการจ๊อบงาน",
                          };
                        })()}
                        onClick={() => setSelectedRowKey(getRowFocusKey(row))}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                      >
                        {row.Contract_No}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td className="text-start">{row.ServiceAddr_Name || "-"}</td>
                  <td className="text-start">{row.ServiceName || "-"}</td>
                  <td>{getStatusBadge(getRowStatus(row))}</td>
                  <td>{formatDate(row.ServiceDate, getRowStatus(row))}</td>
                  <td>{row.AssignedTime || "-"}</td>

                  <td className="sticky-action">
                    <div className="d-flex gap-2 justify-center">
                      <button
                        className="text-blue-600 hover:text-blue-800"
                        onClick={() => {
                          const focusKey = getRowFocusKey(row);
                          setSelectedRowKey(focusKey);
                          onView?.(row, {
                            backLink: buildJobManagementBackLink(focusKey),
                            backLabel: "ตารางงานทั้งหมด",
                          });
                        }}
                        title="ดู"
                      >
                        <Icon icon="mdi:eye" width={20} />
                      </button>
                      {onEdit && (
                        <button
                          className="btn btn-sm btn-warning text-white px-2 py-1"
                          style={{ 
                            backgroundColor: '#f59e0b',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            minWidth: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onClick={() => {
                            const focusKey = getRowFocusKey(row);
                            setSelectedRowKey(focusKey);
                            onEdit?.(row, {
                              backLink: buildJobManagementBackLink(focusKey),
                              backLabel: "ตารางงานทั้งหมด",
                            });
                          }}
                          title="แก้ไข"
                        >
                          <Icon icon="mdi:pencil" width={18} />
                        </button>
                      )}
                      {onDelete && (
                        <button
                          className="btn btn-sm text-white px-2 py-1"
                          style={{ 
                            backgroundColor: '#ef4444',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            minWidth: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onClick={() => onDelete?.(row)}
                          title="ลบ"
                        >
                          <Icon icon="mdi:delete" width={18} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={11} className="text-center py-4 text-gray-500">
                  ไม่มีข้อมูลงาน
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ flexShrink: 0 }}>
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={currentPage}
          onItemsPerPageChange={(val) => {
            setItemsPerPage(val);
            setCurrentPage(1);
          }}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
}

